import { getFirstTouch, type AttributionBundle } from "./attribution";
import { resolveMetaClickIds } from "./metaPixel";
import { getAnalyticsClientId } from "./telemetry";

/**
 * Per-user analytics identifiers, written to
 * `users/{uid}/private/analytics` so server-side conversion events
 * (the subscription trigger in `functions/src/analytics/`) can join the
 * same GA4 user timeline and Meta attribution as the browser.
 *
 * - `gaClientId`: GA4 Measurement Protocol requires a `client_id`.
 * - `fbp` / `fbc`: Meta CAPI click ids, unhashed.
 * - `firstTouch*`: the sticky first-touch UTMs, for revenue by campaign.
 *
 * Owner-writable, allow-listed keys only (see `firestore.rules`). Written
 * at most once per uid per page load, and skipped when nothing changed.
 */

export interface AnalyticsIdentityDoc {
  gaClientId: string | null;
  fbp: string | null;
  fbc: string | null;
  userAgent: string | null;
  firstTouchSource: string | null;
  firstTouchMedium: string | null;
  firstTouchCampaign: string | null;
  firstTouchContent: string | null;
  firstTouchAt: number | null;
  updatedAt: number;
}

function clip(v: string | null | undefined, max: number): string | null {
  if (typeof v !== "string" || v.length === 0) return null;
  return v.slice(0, max);
}

export function buildAnalyticsIdentity(input: {
  gaClientId: string | null;
  fbp?: string;
  fbc?: string;
  userAgent: string | null;
  firstTouch: AttributionBundle | null;
  now: number;
}): AnalyticsIdentityDoc {
  const ft = input.firstTouch;
  return {
    gaClientId: clip(input.gaClientId, 128),
    fbp: clip(input.fbp, 128),
    fbc: clip(input.fbc, 256),
    userAgent: clip(input.userAgent, 512),
    firstTouchSource: clip(ft?.utm_source, 200),
    firstTouchMedium: clip(ft?.utm_medium, 200),
    firstTouchCampaign: clip(ft?.utm_campaign, 200),
    firstTouchContent: clip(ft?.utm_content, 200),
    firstTouchAt: typeof ft?.at === "number" ? ft.at : null,
    updatedAt: input.now,
  };
}

/** True when the ids worth persisting differ; `updatedAt` alone never is. */
export function identityChanged(
  prev: AnalyticsIdentityDoc | null,
  next: AnalyticsIdentityDoc
): boolean {
  if (!prev) return true;
  const keys: (keyof AnalyticsIdentityDoc)[] = [
    "gaClientId",
    "fbp",
    "fbc",
    "userAgent",
    "firstTouchSource",
    "firstTouchMedium",
    "firstTouchCampaign",
    "firstTouchContent",
    "firstTouchAt",
  ];
  return keys.some((k) => prev[k] !== next[k]);
}

const persistedFor = new Set<string>();

export async function persistAnalyticsIdentity(uid: string): Promise<void> {
  if (persistedFor.has(uid)) return;
  persistedFor.add(uid);
  try {
    const { isFirebaseConfigured, getFirebaseFirestore } = await import("./firebase");
    if (!isFirebaseConfigured()) return;
    const db = getFirebaseFirestore();
    if (!db) return;
    const [gaClientId, clickIds] = await Promise.all([
      getAnalyticsClientId(),
      resolveMetaClickIds(),
    ]);
    const next = buildAnalyticsIdentity({
      gaClientId,
      fbp: clickIds.fbp,
      fbc: clickIds.fbc,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      firstTouch: getFirstTouch(),
      now: Date.now(),
    });
    const { doc, getDoc, setDoc } = await import("firebase/firestore");
    const ref = doc(db, "users", uid, "private", "analytics");
    const snap = await getDoc(ref);
    const prev = snap.exists() ? (snap.data() as AnalyticsIdentityDoc) : null;
    // Keep the ids we already have when this browser can't supply them
    // (ad blocker, analytics off), so a later device doesn't erase them.
    const merged: AnalyticsIdentityDoc = prev
      ? {
          ...next,
          gaClientId: next.gaClientId ?? prev.gaClientId ?? null,
          fbp: next.fbp ?? prev.fbp ?? null,
          fbc: next.fbc ?? prev.fbc ?? null,
          firstTouchSource: prev.firstTouchSource ?? next.firstTouchSource,
          firstTouchMedium: prev.firstTouchMedium ?? next.firstTouchMedium,
          firstTouchCampaign: prev.firstTouchCampaign ?? next.firstTouchCampaign,
          firstTouchContent: prev.firstTouchContent ?? next.firstTouchContent,
          firstTouchAt: prev.firstTouchAt ?? next.firstTouchAt,
        }
      : next;
    if (!identityChanged(prev, merged)) return;
    await setDoc(ref, merged);
  } catch {
    // analytics plumbing must never surface to the user
  }
}

/** Test-only. */
export function _resetAnalyticsIdentityForTests(): void {
  persistedFor.clear();
}
